/*
 * Detalhe do cliente: histórico, bloqueio e os direitos do titular.
 *
 * As duas ações da direita são de peso diferente e a tela precisa mostrar isso.
 * Bloquear se desfaz num clique; anonimizar não — por isso pede confirmação
 * digitada e diz na frente o que vai embora e o que fica.
 */
import { useEffect, useState } from 'react';
import { PERMISSIONS } from '@hotspot/shared';
import { ApiRequestError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { CustomerDetailDto } from '../lib/types';
import { Etiqueta, dataCurta, formatarTelefone } from './CustomersPage';
import { Alert, Button, Field, Spinner } from '../components/ui';

interface Props {
  customerId: number;
  onClose: () => void;
  /** Avisa a listagem para refazer a busca. */
  onMudou: () => void;
}

const ROTULO_STATUS: Record<string, string> = {
  pending: 'Pendente',
  authorized: 'Autorizada',
  active: 'Ativa',
  closed: 'Encerrada',
  denied: 'Negada',
  expired: 'Expirada',
};

const ROTULO_METODO: Record<string, string> = {
  open: 'Aberto',
  register: 'Cadastro',
  hubsoft: 'HubSoft',
};

export function CustomerPanel({ customerId, onClose, onMudou }: Props) {
  const { can } = useAuth();
  const podeBloquear = can(PERMISSIONS.CUSTOMER_BLOCK);
  const podeExportar = can(PERMISSIONS.CUSTOMER_EXPORT);
  const podeAnonimizar = can(PERMISSIONS.LGPD_MANAGE);

  const [cliente, setCliente] = useState<CustomerDetailDto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [confirmacao, setConfirmacao] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    setCliente(null);

    apiRequest<CustomerDetailDto>(`/admin/customers/${customerId}`)
      .then((dados) => ativo && setCliente(dados))
      .catch(
        (caught: unknown) =>
          ativo &&
          setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar'),
      );

    return () => {
      ativo = false;
    };
  }, [customerId]);

  async function alterarBloqueio(blocked: boolean) {
    setOcupado(true);
    setErro(null);
    try {
      setCliente(
        await apiRequest<CustomerDetailDto>(`/admin/customers/${customerId}/block`, {
          method: 'POST',
          body: { blocked, ...(blocked && motivo.trim() ? { reason: motivo.trim() } : {}) },
        }),
      );
      setMotivo('');
      onMudou();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao salvar');
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Remove o vinculo cliente/aparelho, que e o que o portal consulta para
   * reconhecer quem volta. Nao apaga o cadastro — so o atalho de reconexao.
   */
  async function esquecerAparelhos() {
    setOcupado(true);
    setErro(null);
    try {
      await apiRequest(`/admin/customers/${customerId}/forget-devices`, {
        method: 'POST',
        body: {},
      });
      setCliente(await apiRequest<CustomerDetailDto>(`/admin/customers/${customerId}`));
      onMudou();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao aplicar');
    } finally {
      setOcupado(false);
    }
  }

  async function anonimizar() {
    setOcupado(true);
    setErro(null);
    try {
      await apiRequest(`/admin/customers/${customerId}/anonymize`, { method: 'POST' });
      setCliente(await apiRequest<CustomerDetailDto>(`/admin/customers/${customerId}`));
      setConfirmacao(null);
      onMudou();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao anonimizar');
    } finally {
      setOcupado(false);
    }
  }

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!cliente) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="text-base font-semibold text-slate-800">Carregando detalhes do cliente...</h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Fechar
            </Button>
          </div>
          {erro ? <Alert>{erro}</Alert> : <Spinner />}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-3 sm:p-6 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl p-6 sm:p-8 space-y-6 my-auto">
        <header className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {cliente.name ?? 'Cliente anonimizado'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              ID #{cliente.id} &bull; Primeiro acesso em {dataCurta(cliente.firstSeenAt)}
            </p>
          </div>
          <div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </header>
      <div className="space-y-6">
        {erro ? <Alert>{erro}</Alert> : null}

        {cliente.isAnonymized ? (
          <Alert tone="info">
            Os dados pessoais deste cliente foram removidos a pedido do titular. O histórico de
            acessos permanece para as estatísticas, sem identificação.
          </Alert>
        ) : null}

        {cliente.isBlocked ? (
          <Alert tone="warning">
            <strong>Bloqueado.</strong>
            {cliente.blockedReason ? ` Motivo: ${cliente.blockedReason}.` : ''} O bloqueio vale a
            partir da próxima conexão.
          </Alert>
        ) : null}

        {/* ---- Resumo ---------------------------------------------------- */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Dado rotulo="Telefone" valor={cliente.phone ? formatarTelefone(cliente.phone) : '—'} />
          <Dado rotulo="Acessos" valor={String(cliente.accessCount)} />
          <Dado rotulo="Primeiro acesso" valor={dataCurta(cliente.firstSeenAt)} />
          <Dado rotulo="Último acesso" valor={dataCurta(cliente.lastSeenAt)} />
        </div>

        {!cliente.isAnonymized ? (
          <p className="text-sm text-slate-600">
            Comunicações de marketing:{' '}
            {cliente.consentMarketing ? (
              <Etiqueta tom="emerald">autorizadas</Etiqueta>
            ) : (
              <Etiqueta tom="slate">não autorizadas</Etiqueta>
            )}
          </p>
        ) : null}

        {/* ---- Aparelhos ------------------------------------------------- */}
        <Secao titulo="Aparelhos" contagem={cliente.devices.length}>
          {cliente.devices.length === 0 ? (
            <Vazio>Nenhum aparelho vinculado.</Vazio>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {cliente.devices.map((aparelho) => (
                <li key={aparelho.id} className="flex flex-wrap items-center gap-2">
                  <code className="font-mono text-xs text-slate-700">{aparelho.mac}</code>
                  {aparelho.isRandomizedMac ? (
                    <Etiqueta tom="slate">endereço aleatório</Etiqueta>
                  ) : null}
                  {aparelho.isBlocked ? <Etiqueta tom="red">bloqueado</Etiqueta> : null}
                  <span className="text-xs text-slate-500">
                    visto em {dataCurta(aparelho.lastSeenAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs text-slate-500">
            Celulares trocam de endereço MAC por rede. Um mesmo cliente pode aparecer com vários
            aparelhos — é esperado, não é duplicidade.
          </p>

          {podeBloquear && !cliente.isAnonymized && cliente.devices.length > 0 ? (
            <div className="mt-3">
              <Button variant="ghost" disabled={ocupado} onClick={() => void esquecerAparelhos()}>
                Exigir novo cadastro
              </Button>
              <p className="mt-1 text-xs text-slate-500">
                Desfaz o reconhecimento: na próxima conexão o cliente preenche o formulário de
                novo, em vez de só confirmar. O cadastro, o histórico e os aceites permanecem.
              </p>
            </div>
          ) : null}
        </Secao>

        {/* ---- Aceites --------------------------------------------------- */}
        <Secao titulo="Termos aceitos" contagem={cliente.acceptances.length}>
          {cliente.acceptances.length === 0 ? (
            <Vazio>Nenhum aceite registrado.</Vazio>
          ) : (
            <ul className="space-y-1.5 text-sm text-slate-700">
              {cliente.acceptances.map((aceite) => (
                <li key={aceite.id}>
                  {aceite.title} <span className="text-slate-500">versão {aceite.version}</span>
                  <span className="ml-2 text-xs text-slate-500">
                    em {dataCurta(aceite.acceptedAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Secao>

        {/* ---- Acessos --------------------------------------------------- */}
        <Secao titulo="Últimos acessos" contagem={cliente.sessions.length}>
          {cliente.sessions.length === 0 ? (
            <Vazio>Nenhum acesso registrado.</Vazio>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs tracking-wide text-slate-500 uppercase">
                    <th className="py-2 pr-3">Quando</th>
                    <th className="py-2 pr-3">Equipamento</th>
                    <th className="py-2 pr-3">VLAN</th>
                    <th className="py-2 pr-3">Modo</th>
                    <th className="py-2 pr-3">IP</th>
                    <th className="py-2">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {cliente.sessions.map((sessao) => (
                    <tr key={sessao.id} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-3 text-slate-700">{dataCurta(sessao.startedAt)}</td>
                      <td className="py-2 pr-3 text-slate-700">{sessao.mikrotikName ?? '—'}</td>
                      <td className="py-2 pr-3 font-mono text-xs text-slate-600">
                        {sessao.vlan ?? <span className="font-sans text-slate-400">—</span>}
                      </td>
                      <td className="py-2 pr-3 text-slate-600">
                        {ROTULO_METODO[sessao.authMethod] ?? sessao.authMethod}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs text-slate-600">
                        {sessao.ip ?? '—'}
                      </td>
                      <td className="py-2 text-slate-600">
                        {ROTULO_STATUS[sessao.status] ?? sessao.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Secao>

        {/* ---- Ações ----------------------------------------------------- */}
        <div className="space-y-4 border-t border-slate-200 pt-5">
          {podeBloquear && !cliente.isAnonymized ? (
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Bloqueio</h3>
              {cliente.isBlocked ? (
                <div className="mt-2">
                  <Button variant="ghost" disabled={ocupado} onClick={() => void alterarBloqueio(false)}>
                    Liberar acesso
                  </Button>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <div className="min-w-[240px] flex-1">
                    <Field
                      label="Motivo (opcional)"
                      value={motivo}
                      onChange={(evento) => setMotivo(evento.target.value)}
                      placeholder="Ex.: uso indevido da rede"
                      hint="Fica registrado na auditoria e aparece para quem atender depois."
                    />
                  </div>
                  <Button variant="danger" disabled={ocupado} onClick={() => void alterarBloqueio(true)}>
                    Bloquear
                  </Button>
                </div>
              )}
            </div>
          ) : null}

          {podeExportar || podeAnonimizar ? (
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Direitos do titular (LGPD)</h3>

              <div className="mt-2 flex flex-wrap gap-2">
                {podeExportar ? (
                  <a
                    href={`/api/admin/customers/${customerId}/export`}
                    className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Exportar dados
                  </a>
                ) : null}

                {podeAnonimizar && !cliente.isAnonymized ? (
                  <Button variant="ghost" onClick={() => setConfirmacao('')}>
                    Anonimizar
                  </Button>
                ) : null}
              </div>

              {confirmacao !== null ? (
                <div className="mt-3 space-y-3 rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm font-semibold text-red-900">
                    Esta ação não pode ser desfeita.
                  </p>

                  <div className="grid gap-3 text-sm text-red-900 sm:grid-cols-2">
                    <div>
                      <p className="font-medium">Será removido</p>
                      <ul className="mt-1 list-inside list-disc text-red-800">
                        <li>Nome e telefone</li>
                        <li>IP e MAC guardados no aceite dos termos</li>
                        <li>O vínculo com os aparelhos</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium">Será mantido</p>
                      <ul className="mt-1 list-inside list-disc text-red-800">
                        <li>Contagem de acessos e datas, sem identificação</li>
                        <li>Quais versões dos termos foram aceitas</li>
                        <li>Registros de conexão, por obrigação legal</li>
                      </ul>
                    </div>
                  </div>

                  <p className="text-sm text-red-900">
                    Se este cliente estiver bloqueado, o bloqueio deixa de valer: sem telefone
                    gravado, um novo cadastro dele vira outro cliente.
                  </p>

                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[220px] flex-1">
                      <Field
                        label='Digite ANONIMIZAR para confirmar'
                        value={confirmacao}
                        onChange={(evento) => setConfirmacao(evento.target.value)}
                      />
                    </div>
                    <Button
                      variant="danger"
                      disabled={ocupado || confirmacao.trim().toUpperCase() !== 'ANONIMIZAR'}
                      onClick={() => void anonimizar()}
                    >
                      {ocupado ? 'Anonimizando...' : 'Anonimizar definitivamente'}
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmacao(null)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  </div>
);
}

function Dado({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">{rotulo}</p>
      <p className="mt-1 text-sm font-semibold text-slate-800">{valor}</p>
    </div>
  );
}

function Secao({
  titulo,
  contagem,
  children,
}: {
  titulo: string;
  contagem: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-slate-800">
        {titulo} <span className="font-normal text-slate-500">({contagem})</span>
      </h3>
      {children}
    </div>
  );
}

function Vazio({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-slate-500">{children}</p>;
}
